import { IWhitelabelContext, useWhitelabel } from 'main/framework'
import { formatCurrency } from 'main/util/currency'
import { budgetFilterRange, IFilters } from 'main/util/search'
import * as React from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../../../common'
import { i18nKeyPrefix } from '../../../../i18n'
import { FilterControl } from '../common'
import IFullSearchProps from '../types'
import FilterForm from './FilterForm'

const PRE = `${i18nKeyPrefix}-FILTERS-BUDGET`

export default function BudgetFilter(props: IFullSearchProps) {
  const {
    filters,
    displayForm,
    palette,
    onChangeFilters,
    onRequestCloseForm,
    onRequestDisplayForm,
  } = props

  const whitelabel = useWhitelabel()

  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <div data-pendo="budgetFilterButton">
      <FilterControl
        isActive={isActive(filters)}
        label={label(filters, whitelabel, t)}
        displayForm={displayForm}
        palette={palette}
        onRequestCloseForm={onRequestCloseForm}
        onRequestDisplayForm={onRequestDisplayForm}
        filterForm={
          <FilterForm
            filters={filters}
            onChangeFilters={onChangeFilters}
            onRequestCloseForm={onRequestCloseForm}
          />
        }
      />
    </div>
  )
}

function hasBudget(filters: IFilters) {
  const {
    budget: { max, min },
  } = filters
  return max !== null || min !== null
}

function isActive(filters: IFilters) {
  const { acceptsSubsidy, acceptsWeeklyBilling } = filters
  return hasBudget(filters) || acceptsSubsidy || acceptsWeeklyBilling
}

function formatBudget(filters: IFilters, t: TFunction) {
  const { budget, acceptsWeeklyBilling } = filters

  const min = budget.min || budgetFilterRange.min
  const max = budget.max || budgetFilterRange.max
  const isMax = budget.max === budgetFilterRange.max
  return `${formatCurrency(min)}-${formatCurrency(max)}${isMax ? '+' : ''}/${
    acceptsWeeklyBilling ? t(`${PRE}-FORM-WEEK`) : t(`${PRE}-FORM-MONTH`)
  }`
}

function label(filters: IFilters, whitelabel: IWhitelabelContext, t: TFunction) {
  const { acceptsSubsidy, acceptsWeeklyBilling } = filters

  const subsidyOrVoucher = t(whitelabel.subsidyAltLookup())

  if (!isActive(filters)) return t(`${PRE}-LABEL`, { subsidyOrVoucher })
  if (!hasBudget(filters) && acceptsSubsidy) {
    return t(`${PRE}-SUBSIDY-ACCEPTED`, { subsidyOrVoucher })
  }
  if (!hasBudget(filters) && acceptsWeeklyBilling) return t(`${PRE}-WEEKLY-ACCEPTED`)
  if (hasBudget(filters) && acceptsSubsidy) {
    return t(`${PRE}-SUBSIDY-ACCEPTED-WITH-BUDGET`, {
      budget: formatBudget(filters, t),
      subsidyOrVoucher,
    })
  }
  if (hasBudget(filters) && acceptsWeeklyBilling) {
    return t(`${PRE}-WEEKLY-ACCEPTED-WITH-BUDGET`, {
      budget: formatBudget(filters, t),
      subsidyOrVoucher,
    })
  }

  return formatBudget(filters, t)
}
